import axios from 'axios';
import Swal from 'sweetalert2';
import collect from 'collect.js';
import FormHelperMixin from '@SHARED/js/Vue/mixins/FormHelperMixin';
import { formatPriceDataLayer } from '@SHARED/js/helpers/misc';
import template from './CheckoutModule.html';
import store from '../../store/store';
import CustomerUserLoginComponent from '../../components/CustomerUserLogin/CustomerUserLoginComponent';
import RegisterCustomerUserComponent from '../../components/RegisterCustomerUser/RegisterCustomerUserComponent';
import CustomerDeliveryFormComponent from '../../components/CustomerDeliveryForm/CustomerDeliveryFormComponent';
import CustomerInvoiceFormComponent from '../../components/CustomerInvoiceForm/CustomerInvoiceFormComponent';
import FormStepsComponent from '../../components/FormSteps/FormStepsComponent';
import ShippingMethodComponent from '../../components/ShippingMethod/ShippingMethodComponent';
import PaymentMethodComponent from '../../components/PaymentMethod/PaymentMethodComponent';
import OrderSummaryComponent from '../../components/OrderSummary/OrderSummaryComponent';
import OrderTermsModalComponent from '../../components/OrderTermsModal/OrderTermsModalComponent';
import ProductCartItem from '../../store/ProductCartItem';

export default {
  template,

  mixins: [FormHelperMixin],

  components: {
    CustomerUserLoginComponent,
    RegisterCustomerUserComponent,
    FormStepsComponent,
    CustomerDeliveryFormComponent,
    CustomerInvoiceFormComponent,
    ShippingMethodComponent,
    PaymentMethodComponent,
    OrderSummaryComponent,
    OrderTermsModalComponent,
  },

  props: {
    initialStep: {
      type: Number,
      default: 1,
    },
  },

  data() {
    return {
      currentStep: this.initialStep,
      productList: [],
      steps: [
        {
          title: 'Logg inn',
          slug: '',
          showLoginForm: true,
          showRegisterCompanyForm: false,
        },
        {
          title: 'Adresse',
          slug: 'adresse',
          isEditing: false,
        },
        {
          title: 'Betaling',
          slug: 'betaling',
          isEditing: false,
        },
        {
          title: 'Bekreftelse',
          slug: 'bekreftelse',
        },
        {
          title: 'Ferdig',
          slug: 'ferdig',
        },
      ],
      sharedState: store, // Do not modify this directly – this is only for convenience in templates.
      isFetching: true,
      isFinalized: false,
      hasAcceptedTerms: false,
    };
  },

  watch: {
    currentStep() {
      this.clearErrors();
    },
  },

  beforeCreate() {
    this.isFetching = true;

    axios.post('/api/v1/customer-user-auth/get-user')
      .then((response) => {
        store.addAuthUser(response.data.user);
      })
      .catch(() => {
        store.removeAuthUser();
      })
      .finally(() => {
        this.isFetching = false;
      });
  },

  created() {
    this.productList = collect(this.sharedState.cart.items).map(
      product => (new ProductCartItem(product)).getDataLayerObject(),
    ).toArray();
    this.submitDataLayerCheckout();
  },

  methods: {
    step1SuccessHandler(data) {
      if (data.user) {
        store.addAuthUser(data.user);
        this.steps[0].showLoginForm = true;
      }

      this.currentStep = 2;
      this.submitDataLayerCheckout();
      window.location.hash = this.steps[1].slug;
      this.scrollToTop();
    },

    step2SuccessHandler() {
      const shippingData = this.$refs.shippingMethod.getData();
      const delivery = this.$refs.customerDeliveryForm.isReady();

      this.clearErrors();

      // Pass?
      if (!shippingData) {
        this.addAttrError('shippingData', 'Leveringsmetode er ikke valgt.');
      }

      if (!delivery) {
        this.addAttrError('deliveryData', 'Leveringsadresse informasjon mangler.');
      }

      if (!shippingData || !delivery) {
        return;
      }

      // We need this in the next steps
      store.info.shippingProduct = shippingData;

      this.currentStep = 3;
      this.submitDataLayerCheckout();
      window.location.hash = this.steps[2].slug;
      this.scrollToTop();
    },

    step3SuccessHandler() {
      if (!this.$refs.customerInvoiceForm.isReady()) {
        Swal.fire(
          'Ikke godkjent',
          'Fakturainformasjon ikke godkjent. Sjekk at feltene er riktige.',
          'warning',
        );

        return;
      }

      this.isFetching = false;
      this.currentStep = 4;
      this.submitDataLayerCheckout();
      window.location.hash = this.steps[3].slug;
      this.scrollToTop();
    },

    step4SuccessHandler(order) {
      this.currentStep = 5;

      this.isFinalized = true;
      window.location.hash = this.steps[4].slug;
      this.submitDataLayerPurchase(order);
      store.clearCart();
      this.scrollToTop();
    },

    submitOrder() {
      if (!this.hasAcceptedTerms) {
        return;
      }

      this.isFetching = true;

      const orderData = Object.assign(
        {},
        store.cart,
        { deliveryData: store.customerDeliveryData },
        { shippingProduct: store.info.shippingProduct },
        { paymentMethod: store.info.paymentMethod },
      );

      axios.post('/api/v1/payment/create-order', orderData)
        .then((response) => {
          this.step4SuccessHandler(response.data.order);

          Swal.fire({
            title: 'Bestilling gjennomført!',
            type: 'success',
            timer: 2000,
            showConfirmButton: false,
          });
        })
        .catch((error) => {
          if (typeof error.response.data.errors !== 'undefined') {
            Swal.fire('Noe gikk galt', collect(error.response.data.errors).join(), 'error');
            window.bugsnagClient && window.bugsnagClient.notify(error);
          } else {
            Swal.fire(
              'Noe gikk galt',
              'Vennligst prøv igjen. Om problemet vedvarer, ring Kraftex (<a href="tel:69252530">69 25 25 30</a>).<br>' +
              'Feil utenom våre åpningstider melder du <a href="/kontakt-oss" target="_blank">her</a>, så tar vi kontakt med deg.',
              'error',
            );

            window.bugsnagClient && window.bugsnagClient.notify(error);
            throw new Error(error);
          }
        })
        .finally(() => {
          this.isFetching = false;
        });
    },

    refreshShippingMethod(data) {
      this.$refs.shippingMethod.getShippingProducts(
        data.delivery_country,
        data.delivery_postal_code,
        store.getTotalWeight(),
      );
    },

    reloadPaymentMethod() {
      this.isFetching = false;
      this.$refs.paymentMethod.reload();
    },

    logout() {
      store.removeAuthUser();
      store.removePaymentSession();

      this.currentStep = 1;
      window.authUserHash = null;
      window.location.hash = this.steps[0].slug;

      axios.post('/api/v1/customer-user-auth/logout');
    },

    scrollToTop() {
      $('html, body').animate({ scrollTop: 0 }, 'fast');
    },

    submitDataLayerCheckout() {
      window.dataLayer.push({ ecommerce: null });
      window.dataLayer.push({
        event: 'checkout',
        ecommerce: {
          checkout: {
            actionField: { step: this.currentStep + 1 }, // add a step to include the cart as well
            products: this.productList,
          },
        },
      });
    },

    submitDataLayerPurchase(order) {
      try {
        // Get shipping amount by filtering our shipping_fee from order_lines.
        const shippingFee = collect(this.sharedState.orderData.order_lines).filter(
          orderLine => orderLine.type === 'shipping_fee',
        );

        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({
          event: 'purchase',
          ecommerce: {
            currencyCode: this.sharedState.orderData.purchase_currency || 'NOK',
            purchase: {
              actionField: {
                id: order.visma_order_id,
                affiliation: 'Kraftex.no - nettbutikk',
                revenue: formatPriceDataLayer(this.sharedState.orderData.order_amount, true), // Inc tax and shipping
                tax: formatPriceDataLayer(this.sharedState.orderData.order_tax_amount, true),
                shipping: formatPriceDataLayer(shippingFee.items[0].total_amount, true),
              },
              products: this.productList,
            },
            userData: {
              firstname: this.sharedState.info.authUser.firstname,
              lastname: this.sharedState.info.authUser.lastname,
              email: this.sharedState.info.authUser.email,
              mobile: this.sharedState.info.authUser.mobile_prefix + this.sharedState.info.authUser.mobile,
            },
          },
        });
      } catch (e) {
        window.dataLayer.push({ ecommerce: null });
        window.dataLayer.push({ event: 'purchase' });

        window.bugsnagClient && window.bugsnagClient.notify(e);
      }
    },
  },
};
